import React, { Component } from 'react';

import { getSessionID, logger } from '../../../utils/logger';
import {
	ErrorBoundaryErrorType,
	ErrorBoundaryTsPropTypes,
} from './errorBoundary.interface';
import {
	Box,
	Header,
	Link,
	SpaceBetween,
} from '@amzn/awsui-components-console';
import { Button } from '@amzn/awsui-components-console';
import katalMetricsPublisher from 'src/utils/metrics';
import { getRiverLinkWithSessionId } from 'src/utils/CommonUtils';
import './errorBoundary.scss';

const errorMetricsPublisher =
	katalMetricsPublisher.newChildActionPublisherForMethod('errorBoundary');

/**
 * Checks if an error is likely caused by session/auth expiry.
 * This includes chunk load failures (CloudFront 403) and auth errors.
 */
function isSessionExpiredError(error: ErrorBoundaryErrorType): boolean {
	const stack = (error?.stack || '').toLowerCase();
	const errorStr = String(error).toLowerCase();

	return (
		errorStr.includes('loading chunk') ||
		errorStr.includes('loading css chunk') ||
		errorStr.includes('failed to fetch dynamically imported module') ||
		errorStr.includes('authentication failed') ||
		errorStr.includes('session expired') ||
		stack.includes('loading chunk') ||
		errorStr.includes('autherror')
	);
}

class ErrorBoundary extends Component<
	ErrorBoundaryTsPropTypes,
	{ hasError: boolean; isSessionError: boolean }
> {
	constructor(props: ErrorBoundaryTsPropTypes) {
		super(props);
		this.state = { hasError: false, isSessionError: false };
	}

	static getDerivedStateFromError(error: ErrorBoundaryErrorType) {
		return {
			hasError: true,
			isSessionError: isSessionExpiredError(error),
		};
	}

	componentDidCatch(error: ErrorBoundaryErrorType, errorInfo: React.ErrorInfo) {
		errorMetricsPublisher.publishCounterMonitor(
			`${this.props.location.pathname}/errors`,
			1
		);
		errorMetricsPublisher.publishCounterMonitor(
			'error_boundary_invocation_count',
			1
		);

		if (this.state.isSessionError) {
			errorMetricsPublisher.publishCounterMonitor('session_expired_errors', 1);
		}

		logger.error(
			`There is some error showing the webpage [URL : Error : ErrorInfo : sessionId]:::[${
				window.location.href
			} : ${error} : ${errorInfo} : ${getSessionID()}]`
		);
	}

	render() {
		const { hasError, isSessionError } = this.state;
		const { children } = this.props;

		if (hasError) {
			// Show a session-specific message if the error is auth/chunk related
			if (isSessionError) {
				return (
					<Box textAlign="center">
						<SpaceBetween
							className="errorBoundary"
							direction="vertical"
							size={'s'}
						>
							<Box textAlign="center">
								<Header variant="h1">Session Expired</Header>
							</Box>
							<Box fontSize="heading-l">
								Your session has expired. Click the button below to log in again
								and continue where you left off.
							</Box>
							<Box textAlign="center">
								<SpaceBetween direction="horizontal" size={'s'}>
									<Button
										onClick={() => window.location.reload()}
										variant="primary"
									>
										Log In Again
									</Button>
									<Button href="/">Go To HomePage</Button>
								</SpaceBetween>
							</Box>
						</SpaceBetween>
					</Box>
				);
			}

			return (
				<Box textAlign="center">
					<SpaceBetween
						className="errorBoundary"
						direction="vertical"
						size={'s'}
					>
						<Box textAlign="center">
							<Header variant="h1">Oops! Something went wrong.</Header>
						</Box>
						<Box fontSize="heading-l">
							Failed to display the webpage. Please retry again, if issue
							persists reach out to SNOW Team via{' '}
							<Link
								fontSize="heading-l"
								external
								href={getRiverLinkWithSessionId()}
							>
								TT Link
							</Link>
						</Box>
						<Box textAlign="center">
							<SpaceBetween direction="horizontal" size={'s'}>
								<Button href="/" variant="primary">
									Go To HomePage
								</Button>
								<Button onClick={() => window.location.reload()}>Reload</Button>
							</SpaceBetween>
						</Box>
					</SpaceBetween>
				</Box>
			);
		}

		return children;
	}
}

export default ErrorBoundary;
